import { AnyObject, mergeData } from '@hb/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApp } from '../../../contexts/AppContext'
import { useFetchData } from '../../../contexts/db/FetchDataContext'
import { addMetadata } from '../../../utils/data'

export interface UseOptimisticUpdate<T extends AnyObject> {
  data: T | null
  loading: boolean
  error: string | null
  update: (updated: Partial<T>) => Promise<{ success?: string; error?: string }>
}

export const useOptimisticUpdate = <T extends AnyObject>(
  collectionId: string,
  documentId?: string | null,
): UseOptimisticUpdate<T> => {
  const { useDocument, updateItem, fetchItem } = useFetchData()
  const { data, loading, error } = useDocument<T>(collectionId, documentId)
  const [temp, setTemp] = useState<T | null>(null)
  const { appName } = useApp()
  useEffect(() => {
    setTemp(data)
  }, [data])

  const update = useCallback(
    async (updated: Partial<T>) => {
      if (!documentId) return { error: 'No documentId' }
      setTemp(prev =>
        addMetadata(prev ? (mergeData(prev, updated) as T) : (updated as T), appName, !prev),
      )
      // const docRef = doc(db, collectionId, documentId)
      const curr = await fetchItem(`${collectionId}/${documentId}`)
      const submitted = addMetadata(updated, appName, !curr)
      return updateItem(`${collectionId}/${documentId}`, submitted)
        .then(() => ({ success: 'Updated successfully' }))
        .catch(() => ({ error: 'Failed to update' }))
    },
    [collectionId, documentId, appName, fetchItem, updateItem],
  )

  return useMemo(
    () => ({
      data: temp ?? data,
      loading,
      error,
      update,
    }),
    [temp, data, loading, error, update],
  )
}
